import React, { useEffect, useState } from "react";
import { Container, Card, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";

export default function CreateUpdateIncome() {
  const { id } = useParams(); // income id (edit mode)
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  /* ---------------- STATE ---------------- */
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);

  const [banks, setBanks] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  const [form, setForm] = useState({
    bank_account: "",
    name: "",
    income_date: "",
    description: "",
    amount: "",
  });

  /* ---------------- FETCH DROPDOWNS ---------------- */
  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
    };

    try {
      const res = await simplePostCall(
        ApiConfig.incomeDropdowns,
        JSON.stringify(body)
      );

      if (res?.result) {
        setBanks(res.banks || []);
        setIncomeTypes(res.incomeTypes || []);
      }
    } catch {
      toast.error("Failed to load dropdowns");
    }
  };

  /* ---------------- FETCH INCOME (EDIT MODE) ---------------- */
  useEffect(() => {
    if (isEditMode) fetchIncomeDetails();
  }, [id]);

  const fetchIncomeDetails = async () => {
    setLoading(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: Number(id),
    };

    try {
      const res = await simplePostCall(
        ApiConfig.incomeView,
        JSON.stringify(body)
      );

      if (res?.result && res?.data?.length > 0) {
        const data = res.data[0];

        setForm({
          bank_account: data.income_bank_account,
          name: data.income_name,
          income_date: formatToDateOnly(data.income_date),
          description: data.income_description,
          amount: data.income_amount,
        });
      }
    } catch {
      toast.error("Failed to load income details");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- HELPERS ---------------- */
  function formatToDateOnly(isoString) {
  const date = new Date(isoString);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}



  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formElement = e.currentTarget;

    if (!formElement.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    setLoading(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      name: String(form.name),
      bank_account: String(form.bank_account),
      date: form.income_date,
      description: form.description,
      amount: Number(form.amount),
    };

    if (isEditMode) body.id = Number(id);

    try {
      const res = await simplePostCall(
        isEditMode ? ApiConfig.incomeUpdate : ApiConfig.incomeCreate,
        JSON.stringify(body)
      );

      if (res?.result) {
        toast.success(res.message);
        navigate("/accountant/OtherIncomes");
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <Container fluid className="py-4">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="mb-3" style={{backgroundColor:'#fff'}}>
        <Card.Body>
          <h5 className="mb-0">
            {isEditMode ? "Update Income" : "Create Income"}
          </h5>
        </Card.Body>
      </Card>

      {/* FORM */}
      <Card style={{backgroundColor:'#fff'}}>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      Bank Account <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      required
                      name="bank_account"
                      value={form.bank_account}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {banks.map((b) => (
                        <option key={b.bank_id} value={b.bank_id}>
                          {b.bank_account_holder_name} ({b.bank_account_number})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Bank account is required
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="income_date"
                      value={form.income_date}
                      onChange={handleChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Date is required
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      required
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {incomeTypes.map((t) => (
                        <option
                          key={t.income_type_id}
                          value={t.income_type_id}
                        >
                          {t.income_type_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Income name is required
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Description <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      required
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Description is required
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Amount <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      required
                      type="number"
                      min="1"
                      name="amount"
                      value={form.amount}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Valid amount is required
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="text-end">
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
